import React, { useState } from 'react';
import type { ChangeEvent } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Input, Title } from '../../components';
import LogoEN from '../../assets/logo-en-svg';
import LogoAR from '../../assets/logo-ar-svg';
import { BRAND_NAME } from '../../config/brand';
import './index.css';

const { api } = require('@cashew/utils');

const ns = 'new-password';

type ApiError = {
  response?: {
    data?: {
      errorCode?: string;
      message?: string;
    };
  };
};

export default () => {
  const { language } = useSelector((state: any) => state.user);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const history = useHistory();
  const dispatch = useDispatch();
  const isEn = language !== 'ar';
  const Logo = isEn ? LogoEN : LogoAR;
  const year = new Date().getFullYear();

  return (
    <>
      <Helmet bodyAttributes={{ class: `light ${ns}` }}>
        <title>New Password</title>
      </Helmet>
      <div className={ns}>
        <div className={`${ns}__container`}>
          <aside className={`${ns}__left`} aria-hidden="true">
            <Logo className={`${ns}__brand-logo`} />
          </aside>
          <section className={`${ns}__right`}>
            <div className={`${ns}__form-container`}>
              <Logo className={`${ns}__logo--mobile`} />
              <div className={`${ns}__title-box`}>
                <Title>New Password</Title>
              </div>
              <form
                className={`${ns}__form`}
                onSubmit={(event) => {
                  event.preventDefault();
                  if (submitting || !password) return;
                  setSubmitting(true);
                  api
                    .patch({
                      endpoint: 'identity/cashew/password',
                      params: {
                        token: query.get('token'),
                        password,
                      },
                    })
                    .then(() => {
                      history.push(`/${language}/login`);
                    })
                    .catch((e: ApiError) => {
                      const errorCode = e.response?.data?.errorCode;
                      const isTokenError =
                        errorCode === '404.002.007' ||
                        e.response?.data?.message
                          ?.toLowerCase()
                          .includes('token');

                      const toasterTitle = isTokenError
                        ? 'Link expired!'
                        : 'Update password';
                      const toasterMessage = isTokenError
                        ? 'Please request a new reset link.'
                        : e.response?.data?.message || 'An error occurred';

                      dispatch({
                        type: 'OPEN_TOASTER',
                        payload: {
                          toasterTitle,
                          toasterMessage,
                          toasterType: 'error',
                          toasterVisible: true,
                          toasterDismiss: null,
                        },
                      });
                    })
                    .finally(() => setSubmitting(false));
                }}
              >
                <Input
                  name="password"
                  placeholder="New Password"
                  type="password"
                  canReveal
                  value={password}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setPassword(e.target.value);
                  }}
                />
                <ul className={`${ns}__requirements`}>
                  <li>Password needs at least 8 characters</li>
                  <li>
                    Password must have at least one lower and one upper case
                    character
                  </li>
                  <li>Password needs at least one symbol ($, !, @, _, *, etc)</li>
                  <li>Password must include one number or more</li>
                </ul>
                <button
                  type="submit"
                  className="btn btn-black"
                  disabled={submitting || !password}
                >
                  {submitting ? 'Saving…' : 'Save'}
                </button>
              </form>
            </div>
            <footer className={`${ns}__copy`}>
              ©{year} {BRAND_NAME}. All Rights Reserved.
            </footer>
          </section>
        </div>
      </div>
    </>
  );
};
